import React, { useMemo, useState } from 'react';
import { ExternalLink, Link2, Loader2, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import type { DeepLinkCampaign, DeepLinkRoute, DeepLinkCampaignFormValues } from '../../models/MasterAdmin/deepLink';
import type { WhatsAppContextField } from '../../models/MasterAdmin/whatsappHub';
import DeepLinkCampaignFormModal from '../MasterAdmin/deep-links/DeepLinkCampaignFormModal';
import { createDeepLinkCampaign, fetchDeepLinkRoutes } from '../../services/MasterAdmin/deepLinkService';
import WhatsAppSearchSelect from './WhatsAppSearchSelect';
import { mergeNotificationFields, notificationFieldSelectOptions } from './whatsappNotificationFields';

import { previewDeepLinkFromCampaign, type DeepLinkAssignmentValue } from './whatsappDeepLinkPreview';

export type { DeepLinkAssignmentValue };

type Props = {
  value: DeepLinkAssignmentValue;
  onChange: (next: DeepLinkAssignmentValue) => void;
  contextFields: WhatsAppContextField[];
  campaigns: DeepLinkCampaign[];
  onCampaignCreated: (campaign: DeepLinkCampaign) => void;
  onCampaignUpdated?: (campaign: DeepLinkCampaign) => void;
  onRefreshCampaigns?: () => void;
  suggestedCampaignName?: string;
  compact?: boolean;
};

type CampaignFilter = 'all' | 'user' | 'plus' | 'doctors' | 'blogs';

const FILTERS: { id: CampaignFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'user', label: 'User app' },
  { id: 'plus', label: 'Vendor app' },
  { id: 'doctors', label: 'Doctors' },
  { id: 'blogs', label: 'Blogs' },
];

function campaignLabel(c: DeepLinkCampaign) {
  const route = c.routeSlug ? ` · ${c.routeSlug}` : '';
  const code = c.shortCode ? ` (${c.shortCode})` : '';
  return `${c.name}${route}${code}`;
}

function entityTypeOf(c: DeepLinkCampaign): string {
  const meta = c.metadata;
  if (meta && typeof meta === 'object' && 'entityType' in meta) {
    return String((meta as { entityType?: unknown }).entityType || '').toLowerCase();
  }
  return '';
}

function matchesFilter(c: DeepLinkCampaign, filter: CampaignFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'user') return (c.targetAppKey || 'user') === 'user';
  if (filter === 'plus') return c.targetAppKey === 'plus';
  if (filter === 'doctors') {
    return entityTypeOf(c) === 'doctor' || c.routeSlug === 'doctor-profile';
  }
  if (filter === 'blogs') {
    return entityTypeOf(c) === 'blog' || c.routeSlug === 'blog' || c.routeSlug === 'blogs';
  }
  return true;
}

const NotificationDeepLinkPicker: React.FC<Props> = ({
  value,
  onChange,
  contextFields,
  campaigns,
  onCampaignCreated,
  onRefreshCampaigns,
  suggestedCampaignName = '',
  compact = false,
}) => {
  const [showCreate, setShowCreate] = useState(false);
  const [routes, setRoutes] = useState<DeepLinkRoute[]>([]);
  const [routesLoading, setRoutesLoading] = useState(false);
  const [filter, setFilter] = useState<CampaignFilter>('all');

  const selected = useMemo(
    () => campaigns.find((c) => c.id === value.deepLinkCampaignId) || null,
    [campaigns, value.deepLinkCampaignId]
  );

  const filteredCampaigns = useMemo(
    () => campaigns.filter((c) => matchesFilter(c, filter)),
    [campaigns, filter]
  );

  const campaignOptions = useMemo(() => {
    const list =
      selected && !filteredCampaigns.some((c) => c.id === selected.id)
        ? [selected, ...filteredCampaigns]
        : filteredCampaigns;
    return list.map((c) => ({
      value: c.id,
      label: campaignLabel(c),
      hint: [c.shortUrl, c.shortCode, c.routeSlug].filter(Boolean).join(' · '),
      group: c.targetAppKey === 'plus' ? 'Vendor app' : 'User app',
    }));
  }, [filteredCampaigns, selected]);

  const paramFieldOptions = useMemo(
    () =>
      notificationFieldSelectOptions(
        mergeNotificationFields(contextFields),
        Object.values(value.deepLinkParamBindings || {})
      ),
    [contextFields, value.deepLinkParamBindings]
  );

  const paramSchema = selected?.route?.paramSchema || [];

  const openCreate = async () => {
    setShowCreate(true);
    onRefreshCampaigns?.();
    if (routes.length) return;
    setRoutesLoading(true);
    try {
      setRoutes(await fetchDeepLinkRoutes(true));
    } catch {
      toast.error('Failed to load deep link routes');
    } finally {
      setRoutesLoading(false);
    }
  };

  const handleCreate = async (formValues: DeepLinkCampaignFormValues) => {
    const created = await createDeepLinkCampaign(formValues);
    onCampaignCreated(created);
    onRefreshCampaigns?.();
    onChange({
      ...value,
      deepLinkCampaignId: created.id,
      deepLinkUrlType: value.deepLinkUrlType || 'short',
    });
    setShowCreate(false);
    toast.success('Deep link campaign created');
  };

  const setParamBinding = (paramName: string, contextKey: string) => {
    const next = { ...(value.deepLinkParamBindings || {}) };
    if (contextKey) next[paramName] = contextKey;
    else delete next[paramName];
    onChange({ ...value, deepLinkParamBindings: next, deepLinkUrlType: 'dynamic' });
  };

  const previewUrl = previewDeepLinkFromCampaign(value, new Map(campaigns.map((c) => [c.id, c])));

  return (
    <div className={`rounded-lg border border-[#25D366]/30 bg-[#25D366]/5 p-3 dark:border-[#25D366]/20 ${compact ? '' : 'mt-2'}`}>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-xs font-medium text-gray-700 dark:text-gray-200">
          <Link2 className="h-3.5 w-3.5 text-[#25D366]" />
          Deep link
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to="/operations/deep-links"
            target="_blank"
            className="inline-flex items-center gap-1 text-[11px] text-[#009de2] hover:underline"
          >
            Manage links
            <ExternalLink className="h-3 w-3" />
          </Link>
          <button
            type="button"
            onClick={() => void openCreate()}
            className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-[11px] font-medium dark:border-gray-600 dark:bg-gray-800"
          >
            {routesLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
            Create new
          </button>
        </div>
      </div>

      <div className="mb-2 flex flex-wrap gap-1">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id)}
            className={`rounded-md px-2 py-0.5 text-[11px] font-medium ${
              filter === f.id
                ? 'bg-[#128C7E] text-white'
                : 'border border-gray-200 bg-white text-gray-600 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div
        onFocus={() => onRefreshCampaigns?.()}
        onClick={() => onRefreshCampaigns?.()}
      >
        <WhatsAppSearchSelect
          value={value.deepLinkCampaignId || ''}
          onChange={(id) =>
            onChange({
              ...value,
              deepLinkCampaignId: id || null,
              deepLinkUrlType: value.deepLinkUrlType || 'short',
            })
          }
          options={campaignOptions}
          placeholder="Select deep link campaign…"
          emptyLabel="Select deep link campaign…"
          searchPlaceholder="Search name, doctor, blog, code…"
        />
      </div>
      <p className="mt-1 text-[10px] text-gray-400">
        Showing {campaignOptions.length}
        {filter !== 'all' ? ` of ${campaigns.length}` : ''} campaigns
      </p>

      {selected && (
        <>
          <div className="mt-2">
            <label className="text-[11px] font-medium uppercase text-gray-500">Link type at send time</label>
            <select
              value={value.deepLinkUrlType || 'short'}
              onChange={(e) =>
                onChange({
                  ...value,
                  deepLinkUrlType: e.target.value as 'short' | 'full' | 'dynamic',
                })
              }
              className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800"
            >
              <option value="short">Short link (/l/code) — fixed campaign</option>
              <option value="full">Full open URL — fixed campaign</option>
              <option value="dynamic">Dynamic — rebuild URL with notification data</option>
            </select>
          </div>

          {paramSchema.length > 0 && (
            <div className="mt-2 space-y-2">
              <p className="text-[11px] text-gray-500">
                Map route params to notification fields (used with dynamic URLs):
              </p>
              {paramSchema.map((p) => (
                <div key={p.name} className="grid grid-cols-2 gap-2 text-xs">
                  <span className="truncate self-center font-mono text-gray-600 dark:text-gray-300">
                    {p.name}
                    {p.required ? ' *' : ''}
                  </span>
                  <WhatsAppSearchSelect
                    value={value.deepLinkParamBindings?.[p.name] || ''}
                    onChange={(key) => setParamBinding(p.name, key)}
                    options={paramFieldOptions}
                    placeholder="Campaign default / static"
                    emptyLabel="Campaign default / static"
                    searchPlaceholder="Search notification fields…"
                  />
                </div>
              ))}
            </div>
          )}

          {previewUrl && (
            <div className="mt-2 rounded-md bg-white p-2 dark:bg-gray-900">
              <p className="text-[10px] font-medium uppercase text-gray-400">Preview URL</p>
              <p className="mt-0.5 break-all font-mono text-[11px] text-gray-700 dark:text-gray-300">{previewUrl}</p>
            </div>
          )}
        </>
      )}

      {showCreate && (
        <DeepLinkCampaignFormModal
          open={showCreate}
          mode="create"
          routes={routes}
          initial={null}
          onClose={() => setShowCreate(false)}
          onSubmit={handleCreate}
          onRoutesChanged={() => void fetchDeepLinkRoutes(true).then(setRoutes)}
        />
      )}

      {!value.deepLinkCampaignId && suggestedCampaignName && (
        <p className="mt-1 text-[11px] text-gray-400">Suggested name: {suggestedCampaignName}</p>
      )}
    </div>
  );
};

export default NotificationDeepLinkPicker;
